import Phaser from 'phaser';
import { Direction } from '@shared/types/entity';
import { getAllItemTemplates } from '@shared/data/items';
import { addGridSpriteSheet } from './gridSpriteSheet';

/**
 * Sprites de arma (weapon):
 * - Misma estructura lógica que los body:
 *   - 4 filas (abajo, arriba, izquierda, derecha)
 *   - 6 columnas (en left/right la última suele estar vacía)
 * - PERO el tamaño real de cada frame se calcula a partir
 *   del tamaño del archivo (ancho/6, alto/4).
 */
const WEAPON_COLUMNS = 6;
const WEAPON_ROWS = 4;

type DirectionAnimKey = 'down' | 'up' | 'left' | 'right';

function getDirectionKey(dir: Direction): DirectionAnimKey {
  switch (dir) {
    case Direction.DOWN:
      return 'down';
    case Direction.UP:
      return 'up';
    case Direction.LEFT:
      return 'left';
    case Direction.RIGHT:
      return 'right';
    default:
      return 'down';
  }
}

export function getWeaponSpriteKey(weaponId: number): string {
  return `weapon${weaponId}`;
}

/**
 * Resuelve el nombre de archivo del spritesheet equipado para un `weaponId`.
 *
 * Los archivos se nombran por SUBTIPO (`sword1.png`, `dagger1.png`, ...) en vez
 * de `weapon<id>.png` — más legible cuando hay decenas de armas. El mapeo
 * `weaponId → nombre` NO se mantiene a mano: se deriva del campo `icon` que el
 * item ya declara en `shared/data/items.ts` (única fuente de verdad). El texture
 * key de Phaser sigue siendo `weapon<id>` (numérico), así que el render y las
 * animaciones no se enteran del cambio.
 *
 * Si un día un arma necesita un sheet equipado distinto a su icono 2D, agregar
 * un campo `weaponSheet?` al template y priorizarlo acá.
 */
let weaponAssetNameCache: Map<number, string> | null = null;
export function getWeaponAssetName(weaponId: number): string {
  if (!weaponAssetNameCache) {
    weaponAssetNameCache = new Map<number, string>();
    for (const tpl of getAllItemTemplates()) {
      if (tpl.weaponId && tpl.icon && !weaponAssetNameCache.has(tpl.weaponId)) {
        weaponAssetNameCache.set(tpl.weaponId, tpl.icon);
      }
    }
  }
  return weaponAssetNameCache.get(weaponId) ?? `weapon${weaponId}`;
}

function frameName(row: number, col: number): string {
  return `r${row}c${col}`;
}

/**
 * Offset visual por SHEET de arma (en píxeles de mundo; x: +derecha, y: +abajo).
 *
 * Parche para sheets autorados con un canvas distinto al estándar (256×256):
 * como el recorte es por grilla uniforme, un canvas distinto corre la "mano"
 * en TODO el sheet. La solución correcta es re-autorar el asset; este mapa es
 * el ajuste fino en runtime. Keyed por nombre de archivo (ej. 'sword1').
 * Tunear mirando el panel 🎞️ (CharFrameDebugPanel) o la galería de weapons.
 */
const WEAPON_SHEET_OFFSETS: Record<string, { x?: number; y?: number }> = {
  wand1: { x: -2, y: 6 }, // +y = baja (mano 6px alta), -x = izquierda (2px)
  axe2: { x: 1, y: 4 }, // +y = baja: la mano del sheet queda 4px alta
  axe3: { x: 2, y: 9 },
  axe4: { x: 1, y: 7 },
  mace4: { y: 7 }, // +y = baja: corre el arma 7px hacia abajo
};

export function getWeaponSheetOffsetByName(assetName: string): { x: number; y: number } {
  const o = WEAPON_SHEET_OFFSETS[assetName];
  return { x: o?.x ?? 0, y: o?.y ?? 0 };
}

/**
 * Resuelve el offset por sheet desde una texture key de Phaser:
 * `weapon<id>` (arma equipada) o `gwpreview_<name>` (preview de la galería).
 */
export function getWeaponSheetOffsetForTextureKey(textureKey: string): { x: number; y: number } {
  if (textureKey.startsWith('gwpreview_')) {
    return getWeaponSheetOffsetByName(textureKey.slice('gwpreview_'.length));
  }
  const m = /^weapon(\d+)$/.exec(textureKey);
  if (m) {
    return getWeaponSheetOffsetByName(getWeaponAssetName(Number(m[1])));
  }
  return { x: 0, y: 0 };
}

// Devuelve el nombre de frame para el frame "idle" (columna 0) de una dirección.
export function getWeaponIdleFrame(dir: Direction): string {
  let row = 0;
  switch (dir) {
    case Direction.DOWN:
      row = 0;
      break;
    case Direction.UP:
      row = 1;
      break;
    case Direction.LEFT:
      row = 2;
      break;
    case Direction.RIGHT:
      row = 3;
      break;
    default:
      row = 0;
      break;
  }

  return frameName(row, 0);
}

/**
 * Pre-carga en Phaser los spritesheet de weapon para una lista de IDs.
 * Los archivos se resuelven por nombre de subtipo via `getWeaponAssetName`
 * (ej. `client/assets/weapon/anims/sword1.png`), derivado del `icon` del item.
 * Los spritesheets de animación viven en `weapon/anims/`; la raíz `weapon/`
 * queda para los gráficos de item.
 */
export function preloadWeaponSprites(scene: Phaser.Scene, weaponIds: number[]): void {
  const anyScene = scene as any;

  // Adjuntar handler global de FILE_COMPLETE una sola vez por escena
  if (!anyScene.__weaponSheetLoaderAttached) {
    anyScene.__weaponSheetLoaderAttached = true;

    scene.load.on(Phaser.Loader.Events.FILE_COMPLETE, (fileKey: string, _type: string) => {
      if (typeof fileKey !== 'string') return;
      // Esperamos claves del tipo "weaponN-img"
      if (!fileKey.startsWith('weapon') || !fileKey.endsWith('-img')) return;

      const idStr = fileKey.slice(6, -4);
      const id = Number(idStr);
      if (!Number.isFinite(id)) return;

      const sheetKey = getWeaponSpriteKey(id);
      // Si ya existe el spritesheet lógico, no hacemos nada
      if (scene.textures.exists(sheetKey)) return;

      const srcTex = scene.textures.get(fileKey);
      if (!srcTex) return;

      const source = srcTex.getSourceImage() as HTMLImageElement;
      if (!source) return;

      // Cortar la grilla 6×4 con bordes redondeados (frames r{row}c{col}; col 0 =
      // idle, cols 1..5 = walk — mismo layout que el body). Bordes redondeados →
      // sin drift si el PNG no es múltiplo exacto de WEAPON_COLUMNS×WEAPON_ROWS.
      addGridSpriteSheet(scene, sheetKey, source, WEAPON_COLUMNS, WEAPON_ROWS, (row, col) =>
        frameName(row, col)
      );
    });
  }

  for (const id of weaponIds) {
    const sheetKey = getWeaponSpriteKey(id);
    if (scene.textures.exists(sheetKey)) continue;

    const imgKey = `${sheetKey}-img`;
    // Evitar recargar la misma imagen
    if (scene.textures.exists(imgKey)) continue;

    scene.load.image(imgKey, `assets/weapon/anims/${getWeaponAssetName(id)}.png`);
  }
}

// El arma arranca su walk un cuadro DESPUÉS que el body: mientras el body está
// en su primer cuadro (col 0) el arma sigue en idle, y recién en el segundo
// cuadro del body el arma empieza a recorrer su fila (lag fijo de 1 columna).
const WEAPON_WALK_LAG = 0;

// Helper para sincronizar frames por fila/columna desde fuera si se necesita
export function getWeaponFrameFromBodyIndex(bodyFrameIndex: number, bodyColumns: number): string {
  const row = Math.floor(bodyFrameIndex / bodyColumns);
  const col = Math.max(0, (bodyFrameIndex % bodyColumns) - WEAPON_WALK_LAG);
  return frameName(row, col);
}

export function getWeaponAnimKey(weaponId: number, dir: Direction): string {
  const dirKey = getDirectionKey(dir);
  return `weapon${weaponId}_${dirKey}`;
}/**
 * Crea las animaciones de caminar para cada weapon y dirección.
 * DEBE llamarse después de que los spritesheets estén listos.
 */
export function createWeaponAnimations(
  scene: Phaser.Scene,
  weaponIds: number[],
  frameRate: number = 6
): void {
  for (const id of weaponIds) {
    const key = getWeaponSpriteKey(id);
    // Verificar que la textura existe
    if (!scene.textures.exists(key)) continue;
    // Convención BH, idéntica al BodySpriteManager: el walk recorre la fila
    // completa (col 0 incluida). down/up = 6 frames (cols 0..5), left/right = 5
    // frames (cols 0..4). Con frame-lock en el render estas anims no se usan
    // directamente, pero se mantienen coherentes con el body.
    // Down (fila 0, cols 0..5)
    ensureWeaponAnim(scene, getWeaponAnimKey(id, Direction.DOWN), key, 0, 0, 5, frameRate);
    // Up (fila 1, cols 0..5)
    ensureWeaponAnim(scene, getWeaponAnimKey(id, Direction.UP), key, 1, 0, 5, frameRate);
    // Left (fila 2, cols 0..4)
    ensureWeaponAnim(scene, getWeaponAnimKey(id, Direction.LEFT), key, 2, 0, 4, frameRate);
    // Right (fila 3, cols 0..4)
    ensureWeaponAnim(scene, getWeaponAnimKey(id, Direction.RIGHT), key, 3, 0, 4, frameRate);
  }
}function ensureWeaponAnim(
  scene: Phaser.Scene,
  animKey: string,
  spriteKey: string,
  row: number,
  colStart: number,
  colEnd: number,
  frameRate: number
): void {
  if (scene.anims.exists(animKey)) return;  const frames: Phaser.Types.Animations.AnimationFrame[] = [];
  for (let col = colStart; col <= colEnd; col++) {
    const frameName = `r${row}c${col}`;
    if (scene.textures.get(spriteKey).has(frameName)) {
      frames.push({ key: spriteKey, frame: frameName });
    }
  }  if (frames.length > 0) {
    scene.anims.create({
      key: animKey,
      frames: frames,
      frameRate,
      repeat: -1,
    });
  }
}